import { useState } from 'react';
import { Box, Card, CardContent, Typography, IconButton, Collapse, Menu, MenuItem, Chip, Tooltip, Button } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SyncIcon from '@mui/icons-material/Sync';
import WarningIcon from '@mui/icons-material/Warning';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EventIcon from '@mui/icons-material/Event';
import { Link, useNavigate } from 'react-router-dom';
import { Event, PackingOperationList, QueryItemTypesWithLocationsResponse } from '../schema';
import EventInventorySummary from './EventInventorySummary';
import CreateOperationDialog from './CreateOperationDialog';
import LinkedToTripButton from './LinkedToTripButton';

interface Props {
    event: Event;
    itemCount: number;
    trailerCount: number;
    /** Event group name when event is part of a group */
    groupName?: string | null;
    /** Trips (id + name) when event is part of trip(s) – shown as link badges */
    trips?: Array<{ id: string; name: string }>;
    activeOperation: PackingOperationList | null;
    itemTypesData: QueryItemTypesWithLocationsResponse | undefined;
}

export default function ActiveEventRow({
    event,
    itemCount,
    trailerCount,
    groupName: _groupName,
    trips = [],
    activeOperation,
    itemTypesData,
}: Props) {
    const [expanded, setExpanded] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const navigate = useNavigate();

    const now = new Date();
    const isEnded = event.endDate ? new Date(event.endDate) < now : false;
    const daysAgo = event.endDate ? Math.floor((now.getTime() - new Date(event.endDate).getTime()) / (1000 * 60 * 60 * 24)) : 0;
    const isActiveOpSource = activeOperation?.sourceEvents?.some((e: any) => e.id === event.id) || false;
    const isCurrent = event.startDate && event.endDate ? new Date(event.startDate) <= now && new Date(event.endDate) >= now : false;

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleViewEvent = () => {
        handleMenuClose();
        navigate(`/event/${event.id}/inventory`);
    };

    const handleCreateOperation = () => {
        handleMenuClose();
        setCreateDialogOpen(true);
    };

    const handleViewInventory = () => {
        handleMenuClose();
        setExpanded(!expanded);
    };

    return (
        <>
            <Card
                sx={{
                    boxShadow: 2,
                    ...(isEnded &&
                        itemCount > 0 && {
                            bgcolor: theme => alpha(theme.palette.error.main, 0.08),
                        }),
                }}
            >
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {/* Expand/Collapse Icon */}
                        <IconButton size="small" onClick={() => setExpanded(!expanded)}>
                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>

                        {/* Event Info */}
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography
                                variant="h6"
                                component={Link}
                                to={`/event/${event.id}/inventory`}
                                sx={{
                                    fontWeight: 600,
                                    mb: 0.5,
                                    color: 'primary.main',
                                    textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                            >
                                {event.name}
                            </Typography>
                            {trips.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                                    {trips.map(trip => (
                                        <LinkedToTripButton key={trip.id} trip={trip} />
                                    ))}
                                </Box>
                            )}
                            <Typography
                                component="div"
                                variant="body2"
                                color="text.secondary"
                                sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}
                            >
                                {event.startDate && event.endDate
                                    ? `${new Date(event.startDate).toLocaleDateString()} - ${new Date(event.endDate).toLocaleDateString()}`
                                    : event.startDate
                                      ? `Starting ${new Date(event.startDate).toLocaleDateString()}`
                                      : 'No dates'}
                                {isEnded && itemCount > 0 && (
                                    <Chip
                                        label={`Ended ${daysAgo} ${daysAgo === 1 ? 'day' : 'days'} ago`}
                                        size="small"
                                        color="error"
                                        sx={{ ml: 1 }}
                                    />
                                )}
                            </Typography>
                        </Box>

                        {/* Status Badges */}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            {isActiveOpSource && (
                                <Tooltip title="This event is a source in the current packing operation">
                                    <Chip icon={<SyncIcon />} label="Active Operation Source" size="small" color="primary" />
                                </Tooltip>
                            )}
                            {isEnded && itemCount > 0 && (
                                <Tooltip title="Event ended but still has items. Create packing operation to retrieve.">
                                    <Chip icon={<WarningIcon />} label="Items Remaining" size="small" color="error" />
                                </Tooltip>
                            )}
                            {isEnded && daysAgo <= 3 && itemCount > 0 && (
                                <Tooltip title="Event ended recently. Unpack soon.">
                                    <Chip icon={<ScheduleIcon />} label="Ready to Unpack" size="small" color="warning" />
                                </Tooltip>
                            )}
                            {isCurrent && (
                                <Tooltip title="Event is currently active">
                                    <Chip icon={<EventIcon />} label="Current Event" size="small" color="success" />
                                </Tooltip>
                            )}
                        </Box>

                        {/* Item Counts - trip context: Items: X (in N trailers) */}
                        <Box sx={{ textAlign: 'right', minWidth: 120 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                Items: {itemCount}{' '}
                                {trailerCount > 0 ? `(in ${trailerCount} ${trailerCount === 1 ? 'trailer' : 'trailers'})` : ''}
                            </Typography>
                        </Box>

                        {/* Quick Action Button */}
                        <Button variant="outlined" size="small" onClick={handleCreateOperation} sx={{ minWidth: 140 }}>
                            Create Operation
                        </Button>

                        {/* Actions Menu */}
                        <IconButton size="small" onClick={handleMenuOpen}>
                            <MoreVertIcon />
                        </IconButton>
                    </Box>

                    {/* Expanded Inventory Summary */}
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                            <EventInventorySummary event={event} itemTypesData={itemTypesData} />
                        </Box>
                    </Collapse>
                </CardContent>
            </Card>

            {/* Actions Menu */}
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
                <MenuItem onClick={handleViewEvent}>View Event Details</MenuItem>
                <MenuItem onClick={handleCreateOperation}>Create Packing Operation</MenuItem>
                <MenuItem onClick={handleViewInventory}>{expanded ? 'Hide' : 'View'} Inventory Summary</MenuItem>
            </Menu>

            {/* Create Operation Dialog */}
            <CreateOperationDialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} initialSourceEventIds={[event.id]} />
        </>
    );
}
